import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { notifications } from '@mantine/notifications';
import { useIsAuthenticated } from '@/src/modules/auth/hooks/use-is-authenticated';
import { useAuthStore } from '@/src/modules/auth/stores/use-auth-store';
import { useCreateMarketplaceCustomerConversation } from '@/src/modules/chat/hooks/use-create-marketplace-customer-conversation';
import { ApplicationError } from '@/src/shared/errors';
import { useChatStore } from '@/src/shared/stores/use-chat-store';
import { translateWithFallback } from '@/src/shared/utils/i18n';
import { buildRedirectToWhatsappUrl } from '@/src/shared/utils/url';
import { EChatTab } from '../_enums/chat-tab';
import { EChatTabTarget } from '../_enums/chat-tab-target';

interface UseRedirectCustomerChatPageParams {
  branchId: number | null;
  branchUserPhoneNumber?: string | null;
}

export function useRedirectCustomerChat({
  branchId,
  branchUserPhoneNumber,
}: UseRedirectCustomerChatPageParams) {
  const apiErrorTranslator = useTranslations('api-errors');
  const commonErrorTranslator = useTranslations('common-errors');
  const isAuthenticated = useIsAuthenticated();
  const router = useRouter();

  const setInvoiceNumber = useChatStore((state) => state.setInvoiceNumber);

  const userId = useAuthStore((state) => state.user?.props.id);
  const {
    trigger: createCustomerConversationWithSeller,
    isLoading: isCreatingCustomerConversationWithSeller,
  } = useCreateMarketplaceCustomerConversation({
    branchId,
    userId: userId ?? null,
  });

  const openWhatsappMe = () => {
    if (!branchUserPhoneNumber) {
      return;
    }
    const url = buildRedirectToWhatsappUrl({
      countryNumber: 62,
      phoneNumber: branchUserPhoneNumber,
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const redirectSeller = async () => {
    if (!isAuthenticated) {
      router.push('/login');
      return;
    }

    try {
      if (branchId) {
        await createCustomerConversationWithSeller();
        const params = new URLSearchParams({
          tab: EChatTab.WithSeller,
          branch_id: branchId.toString(),
        });
        router.push(`/chat?${params.toString()}`);
        return;
      }
      router.push('/chat');
    } catch (error) {
      if (error instanceof ApplicationError) {
        if (error.message === 'CHAT_INBOX_NOT_FOUND') {
          openWhatsappMe();
          return;
        }

        notifications.show({
          title: error.name,
          message: translateWithFallback(
            error.message,
            apiErrorTranslator,
            commonErrorTranslator
          ),
          color: 'red',
        });
        return;
      }

      throw error;
    }
  };

  const redirectSelfMedication = () => {
    if (!isAuthenticated) {
      router.push('/login');
      return;
    }
    const params = new URLSearchParams({
      tab: EChatTab.Support,
      target: EChatTabTarget.SelfMedication,
    });
    router.push(`/chat?${params.toString()}`);
  };

  const redirectComplaint = (invoiceNumber: string) => {
    if (!isAuthenticated) {
      router.push('/login');
      return;
    }
    setInvoiceNumber(invoiceNumber);
    const params = new URLSearchParams({
      tab: EChatTab.Support,
      target: EChatTabTarget.Complaint,
    });
    router.push(`/chat?${params.toString()}`);
  };

  return {
    redirectSeller,
    redirectSelfMedication,
    redirectComplaint,
    isCreatingCustomerConversationWithSeller,
  };
}
